import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessMetricDictionaryReadModelSchema,
  isBusinessMetricDictionaryReadModel,
} from "./business-metrics.js";

const DashboardIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const DashboardDateSchema = Type.String({
  minLength: 10,
  maxLength: 10,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$",
});
const DashboardDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const DashboardChineseTextSchema = Type.String({
  minLength: 2,
  maxLength: 300,
  pattern: "[\\u3400-\\u9FFF]",
});
const DashboardCountSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const DashboardFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const OwnerDashboardStatusSchema = Type.Union([
  Type.Literal("GREEN"),
  Type.Literal("YELLOW"),
  Type.Literal("RED"),
  Type.Literal("UNAVAILABLE"),
]);
export type OwnerDashboardStatus = Static<typeof OwnerDashboardStatusSchema>;

export const OwnerDashboardDrillTargetSchema = Type.Union([
  Type.Literal("DUTY"),
  Type.Literal("SHIFTS"),
  Type.Literal("TABLES"),
  Type.Literal("ORDERS"),
  Type.Literal("REVIEW"),
  Type.Literal("INVENTORY"),
]);
export type OwnerDashboardDrillTarget = Static<typeof OwnerDashboardDrillTargetSchema>;

export const OwnerDashboardReasonSchema = Type.Object(
  {
    message: DashboardChineseTextSchema,
    nextStep: DashboardChineseTextSchema,
    drillTarget: OwnerDashboardDrillTargetSchema,
  },
  { additionalProperties: false },
);
export type OwnerDashboardReason = Static<typeof OwnerDashboardReasonSchema>;

const OwnerDashboardSectionFields = {
  status: OwnerDashboardStatusSchema,
  reasons: Type.Array(OwnerDashboardReasonSchema, { maxItems: 12 }),
};

export const OwnerDashboardDutySchema = Type.Object(
  {
    ...OwnerDashboardSectionFields,
    serviceDutyStatus: Type.Union([
      Type.Literal("ACTIVE"),
      Type.Literal("INACTIVE"),
      Type.Literal("NOT_AVAILABLE"),
    ]),
    serviceDutyAssignee: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    cashShiftStatus: Type.Union([
      Type.Literal("NOT_STARTED"),
      Type.Literal("OPEN"),
      Type.Literal("HANDOVER_PENDING"),
      Type.Literal("CLOSED"),
      Type.Literal("NOT_AVAILABLE"),
    ]),
    cashShiftOperator: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    incompleteClaimedCount: Type.Union([DashboardCountSchema, Type.Null()]),
    unattendedServiceRequestCount: Type.Union([DashboardCountSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type OwnerDashboardDuty = Static<typeof OwnerDashboardDutySchema>;

export const OwnerDashboardTablesSchema = Type.Object(
  {
    ...OwnerDashboardSectionFields,
    enabledCount: Type.Union([DashboardCountSchema, Type.Null()]),
    inUseCount: Type.Union([DashboardCountSchema, Type.Null()]),
    awaitingCleaningCount: Type.Union([DashboardCountSchema, Type.Null()]),
    warningSessionCount: Type.Union([DashboardCountSchema, Type.Null()]),
    criticalSessionCount: Type.Union([DashboardCountSchema, Type.Null()]),
    tableFeeReceivableFen: Type.Union([DashboardFenSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type OwnerDashboardTables = Static<typeof OwnerDashboardTablesSchema>;

export const OwnerDashboardFundsSchema = Type.Object(
  {
    ...OwnerDashboardSectionFields,
    unavailableMetricCount: DashboardCountSchema,
    partialMetricCount: DashboardCountSchema,
  },
  { additionalProperties: false },
);
export type OwnerDashboardFunds = Static<typeof OwnerDashboardFundsSchema>;

export const OwnerDashboardKeyItemsSchema = Type.Object(
  {
    ...OwnerDashboardSectionFields,
    pendingManualReviewCount: Type.Union([DashboardCountSchema, Type.Null()]),
    manualReviewCapacityExceeded: Type.Union([Type.Boolean(), Type.Null()]),
    syncConflictOrderCount: Type.Union([DashboardCountSchema, Type.Null()]),
    lowStockProductCount: Type.Union([DashboardCountSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type OwnerDashboardKeyItems = Static<typeof OwnerDashboardKeyItemsSchema>;

export const OwnerDashboardStoreComparisonSchema = Type.Object(
  {
    availability: Type.Literal("NOT_AVAILABLE"),
    reason: DashboardChineseTextSchema,
  },
  { additionalProperties: false },
);

export const OwnerOperatingDashboardReadModelSchema = Type.Object(
  {
    tenantId: DashboardIdSchema,
    storeId: DashboardIdSchema,
    businessDate: DashboardDateSchema,
    currentBusinessDate: DashboardDateSchema,
    generatedAt: DashboardDateTimeSchema,
    dictionary: BusinessMetricDictionaryReadModelSchema,
    duty: OwnerDashboardDutySchema,
    tables: OwnerDashboardTablesSchema,
    funds: OwnerDashboardFundsSchema,
    keyItems: OwnerDashboardKeyItemsSchema,
    storeComparison: OwnerDashboardStoreComparisonSchema,
  },
  { additionalProperties: false },
);
export type OwnerOperatingDashboardReadModel = Static<
  typeof OwnerOperatingDashboardReadModelSchema
>;

function hasConsistentSectionStatus(section: {
  status: OwnerDashboardStatus;
  reasons: readonly OwnerDashboardReason[];
}): boolean {
  return section.status === "GREEN" ? section.reasons.length === 0 : section.reasons.length > 0;
}

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isOwnerOperatingDashboardReadModel(
  value: unknown,
  expected?: { tenantId: string; storeId: string; businessDate: string },
): value is OwnerOperatingDashboardReadModel {
  if (!Value.Check(OwnerOperatingDashboardReadModelSchema, value)) return false;
  const dashboard = value as OwnerOperatingDashboardReadModel;
  if (
    !isCanonicalDate(dashboard.businessDate) ||
    !isCanonicalDate(dashboard.currentBusinessDate) ||
    !isCanonicalTimestamp(dashboard.generatedAt) ||
    dashboard.generatedAt !== dashboard.dictionary.generatedAt ||
    !isBusinessMetricDictionaryReadModel(dashboard.dictionary, {
      tenantId: dashboard.tenantId,
      storeId: dashboard.storeId,
      businessDate: dashboard.businessDate,
    }) ||
    dashboard.dictionary.tenantId !== dashboard.tenantId ||
    dashboard.dictionary.storeId !== dashboard.storeId ||
    dashboard.dictionary.businessDate !== dashboard.businessDate ||
    (expected !== undefined &&
      (dashboard.tenantId !== expected.tenantId ||
        dashboard.storeId !== expected.storeId ||
        dashboard.businessDate !== expected.businessDate))
  ) {
    return false;
  }
  if (
    !hasConsistentSectionStatus(dashboard.duty) ||
    !hasConsistentSectionStatus(dashboard.tables) ||
    !hasConsistentSectionStatus(dashboard.funds) ||
    !hasConsistentSectionStatus(dashboard.keyItems)
  ) {
    return false;
  }
  const historical = dashboard.businessDate !== dashboard.currentBusinessDate;
  if (
    historical &&
    (dashboard.duty.status !== "UNAVAILABLE" ||
      dashboard.tables.status !== "UNAVAILABLE" ||
      dashboard.keyItems.status !== "UNAVAILABLE" ||
      dashboard.duty.serviceDutyStatus !== "NOT_AVAILABLE" ||
      dashboard.duty.cashShiftStatus !== "NOT_AVAILABLE" ||
      dashboard.duty.unattendedServiceRequestCount !== null ||
      dashboard.tables.enabledCount !== null ||
      dashboard.tables.inUseCount !== null ||
      dashboard.tables.awaitingCleaningCount !== null ||
      dashboard.tables.warningSessionCount !== null ||
      dashboard.tables.criticalSessionCount !== null ||
      dashboard.keyItems.pendingManualReviewCount !== null ||
      dashboard.keyItems.manualReviewCapacityExceeded !== null ||
      dashboard.keyItems.syncConflictOrderCount !== null ||
      dashboard.keyItems.lowStockProductCount !== null)
  ) {
    return false;
  }
  if (
    !historical &&
    (dashboard.duty.unattendedServiceRequestCount === null ||
      (dashboard.duty.unattendedServiceRequestCount > 0 &&
        (dashboard.duty.status !== "RED" ||
          !dashboard.duty.reasons.some((reason) => reason.drillTarget === "DUTY"))))
  ) {
    return false;
  }
  if (
    dashboard.funds.unavailableMetricCount !==
      dashboard.dictionary.metrics.filter((metric) => metric.availability === "NOT_AVAILABLE")
        .length ||
    dashboard.funds.partialMetricCount !==
      dashboard.dictionary.metrics.filter((metric) => metric.availability === "PARTIAL").length ||
    (dashboard.tables.tableFeeReceivableFen !== null && dashboard.tables.tableFeeReceivableFen < 0)
  ) {
    return false;
  }
  return true;
}
